import React from 'react';
import PropTypes from 'prop-types';
import LoginButton from './LoginButton';
import RecentArticles from './RecentArticles';

export default function ArticlePage({
  htmlContent, postDate, pdfLink, updatedAt, label, slug,
}) {
  const formatDate = (date) => {
    try {
      // Handle Unix timestamps (13-digit milliseconds or 10-digit seconds)
      const timestamp = typeof date === 'string' && /^\d+$/.test(date) ? parseInt(date, 10) : date;
      const dateObj = new Date(timestamp);

      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return date;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 px-4 sm:px-6 lg:py-12 max-w-7xl mx-auto">
        {/* Main article content */}
        <div className="flex-1 min-w-0">
          {/* Page title */}
          {label && (
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {label}
            </h1>
          )}

          {/* Header section with date and accent */}
          <div className="">
            {postDate && (
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full" />
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 text-blue-500 flex-shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span className="font-medium">
                      Posted on
                      {' '}
                      {formatDate(postDate)}
                    </span>
                  </div>
                  {updatedAt && updatedAt !== postDate && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4 text-gray-400 flex-shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span className="font-medium">
                        Updated on
                        {' '}
                        {formatDate(updatedAt)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Article content card */}
          <article
            className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 mb-8 overflow-hidden border border-gray-100 p-6 sm:p-8 md:p-10"
          >
            <div
              className="prose max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:scroll-mt-24
                prose-h1:text-3xl prose-h1:border-b prose-h1:border-gray-100 prose-h1:pb-4 prose-h1:mb-6
                prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-xl
                prose-p:text-gray-700 prose-p:leading-relaxed
                prose-a:text-blue-600 prose-a:hover:text-blue-700 prose-a:font-medium prose-a:border-b prose-a:border-blue-200 prose-a:hover:border-blue-600
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-code:bg-gray-100 prose-code:text-gray-900 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm
                prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:p-4 prose-pre:overflow-x-auto
                prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-700
                prose-ul:list-disc prose-ul:pl-6
                prose-ol:list-decimal prose-ol:pl-6
                prose-li:text-gray-700
                prose-img:rounded-lg prose-img:shadow-md
                prose-table:border-collapse prose-table:w-full
                prose-th:bg-gray-50 prose-th:font-semibold prose-th:border prose-th:border-gray-200 prose-th:px-4 prose-th:py-2
                prose-td:border prose-td:border-gray-200 prose-td:px-4 prose-td:py-2
                prose-hr:border-gray-100 prose-hr:my-8"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </article>

          {/* PDF Download button */}
          {pdfLink && (
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => window.open(pdfLink, '_blank', 'noopener,noreferrer')}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold text-sm shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-800 active:scale-95 transition-all duration-200 group flex-1 sm:flex-auto"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 flex-shrink-0 group-hover:translate-y-1 transition-transform duration-200"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span>Download PDF</span>
              </button>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="w-full lg:w-80 lg:flex-shrink-0 mt-28">
          <div className="sticky top-28 space-y-6">
            <LoginButton />

            <RecentArticles currentSlug={slug} />
          </div>
        </div>
      </div>
    </div>
  );
}

ArticlePage.propTypes = {
  htmlContent: PropTypes.any,
  postDate: PropTypes.string,
  pdfLink: PropTypes.string,
  label: PropTypes.string,
  updatedAt: PropTypes.string,
  slug: PropTypes.string,
};
